import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  IoClose,
  IoCheckmark,
  IoStorefrontOutline,
  IoAdd,
  IoTrashOutline,
  IoCameraOutline,
  IoImageOutline,
} from "react-icons/io5";
import api, { fileUrl } from "@/api";
import { useI18n, ALL_STORES, STORE_LABELS } from "@/i18n";
import { useAuth } from "@/auth";
import { compressImage } from "@/utils/image";
import { tapSuccess, tapError } from "@/utils/haptics";

export default function ProblemFormPage() {
  const { problemId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, isDirettore } = useAuth();
  const camRef = useRef(null);
  const galRef = useRef(null);
  const isNew = !problemId || problemId === "new";

  const [form, setForm] = useState({
    store_id: isDirettore ? (user?.storeId || "") : "",
    title: "",
    description: "",
    priority: "medium",
    status: "open",
    photo_paths: [],
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/problems/${problemId}`);
        setForm(data);
      } catch {
        toast.error("Non trovata");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    })();
  }, [problemId, isNew, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const raw of Array.from(files)) {
        const file = await compressImage(raw, { maxSide: 1800, quality: 0.82 });
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.id);
      }
      set("photo_paths", [...(form.photo_paths || []), ...uploaded]);
    } catch {
      toast.error("Errore upload foto");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.store_id) {
      toast.error("Scegli il punto vendita");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Titolo obbligatorio");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/problems", form);
        tapSuccess();
        toast.success("Segnalazione inviata");
      } else {
        await api.put(`/problems/${problemId}`, form);
        tapSuccess();
        toast.success("Aggiornato");
      }
      // Robust nav: fall back to /problems if there's no history (deep-link entry).
      if (window.history.length > 1) navigate(-1);
      else navigate("/problems");
    } catch (e) {
      tapError();
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const removePhoto = (idx) => {
    const next = [...(form.photo_paths || [])];
    next.splice(idx, 1);
    set("photo_paths", next);
  };

  const canSubmit = useMemo(
    () => Boolean(form.store_id && form.title.trim()),
    [form],
  );

  if (loading) {
    return (
      <div className="mobile-frame">
        <div className="tf-header-simple">
          <button className="tf-close-btn" onClick={() => navigate(-1)}><IoClose size={22} /></button>
          <div className="tf-header-title-center">…</div>
          <div style={{ minWidth: 44 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-frame" data-testid="problem-form-page">
      <div className="tf-header-simple" style={{ borderBottom: "1px solid var(--tf-border)" }}>
        <button data-testid="prf-close" className="tf-close-btn" onClick={() => navigate("/problems")} aria-label={t("cancel")}>
          <IoClose size={22} />
        </button>
        <div className="tf-header-title-center" data-testid="page-title" style={{ textAlign: "left", paddingLeft: 6 }}>
          {isNew ? t("new_problem") : t("edit")}
        </div>
        <div style={{ minWidth: 44 }} />
      </div>

      <form onSubmit={submit} className="tf-content" style={{ paddingTop: 20 }}>
        <div className="tf-field">
          <label className="tf-label">{t("stores_short")} *</label>
          <div className="tf-store-select-field">
            <IoStorefrontOutline size={22} className="icon-left" />
            <select data-testid="prf-store" className="tf-select" value={form.store_id} onChange={(e) => set("store_id", e.target.value)} disabled={isDirettore}>
              <option value="">{t("choose_store")}</option>
              {ALL_STORES.map((s) => <option key={s} value={s}>{t(STORE_LABELS[s])}</option>)}
            </select>
          </div>
        </div>

        <div className="tf-field">
          <label className="tf-label">{t("priority")} *</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            {["low", "medium", "high"].map((prio) => (
              <button
                key={prio}
                type="button"
                data-testid={`prf-prio-${prio}`}
                className={`tf-pill-btn ${form.priority === prio ? "active" : ""}`}
                onClick={() => set("priority", prio)}
              >
                {t(`priority_${prio}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="tf-field">
          <label className="tf-label">{t("problem_title")} *</label>
          <input
            data-testid="prf-title"
            className="tf-input"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder={t("problem_title_placeholder")}
          />
        </div>

        <div className="tf-field">
          <label className="tf-label">{t("description")}</label>
          <textarea
            data-testid="prf-description"
            className="tf-textarea"
            rows={4}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder={t("problem_desc_placeholder")}
          />
        </div>

        {!isNew && (
          <div className="tf-field">
            <label className="tf-label">{t("status")}</label>
            <select data-testid="prf-status" className="tf-select" value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="open">{t("status_open")}</option>
              <option value="in_progress">{t("status_in_progress")}</option>
              <option value="resolved">{t("status_resolved")}</option>
            </select>
          </div>
        )}

        <div className="tf-field">
          <label className="tf-label">{t("photo_optional")}</label>
          <div className="tf-photo-grid">
            {(form.photo_paths || []).map((id, idx) => (
              <div key={id} style={{ position: "relative" }}>
                <img data-testid={`prf-photo-${idx}`} src={fileUrl(id)} alt="" loading="lazy" decoding="async" />
                <button
                  type="button"
                  data-testid={`prf-photo-remove-${idx}`}
                  onClick={() => removePhoto(idx)}
                  style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.6)", color: "white", border: "none", borderRadius: 999, width: 24, height: 24, cursor: "pointer" }}
                >×</button>
              </div>
            ))}
            <button
              type="button"
              data-testid="prf-photo-camera"
              className="tf-photo-add"
              onClick={() => camRef.current?.click()}
            >
              {uploading ? "..." : <IoCameraOutline size={24} />}
            </button>
            <button
              type="button"
              data-testid="prf-photo-gallery"
              className="tf-photo-add"
              onClick={() => galRef.current?.click()}
            >
              <IoImageOutline size={24} />
            </button>
          </div>
          <input ref={camRef} type="file" accept="image/*" capture="environment" multiple style={{ display: "none" }} onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
          <input ref={galRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
        </div>

        <button
          type="submit"
          data-testid="prf-save"
          className="tf-btn-primary sq"
          disabled={saving || !canSubmit}
          style={{ marginTop: 8 }}
        >
          <IoCheckmark size={20} />
          {saving ? t("saving") : t("send_report")}
        </button>
      </form>
    </div>
  );
}
